/**
 * DenominationInput — the 9-row controlled grid for the count voucher.
 *
 * One row per RBI denomination (₹500 / ₹200 / ₹100 / ₹50 / ₹20 / ₹10
 * notes + ₹5 / ₹2 / ₹1 coins). The user types `pieces` per row; the
 * component computes per-row subtotal and the grand total in integer
 * paise (rule §7-7 — no floats).
 *
 * Pure controlled: `value` is the pieces-per-denom map, `onChange`
 * receives the next snapshot whenever a row mutates. The grand total
 * is exposed via `onChange`'s second arg so the composer's preview
 * pane stays in sync without re-computing.
 *
 * @see prototype/cash/petty-cash-count.html `.doc-items` (denomination table)
 */
import { Input, cn } from "@flint/ui";

/** Denomination values in paise — order matches the prototype top-down. */
export const DENOMINATIONS = [50000, 20000, 10000, 5000, 2000, 1000, 500, 200, 100] as const;
export type Denomination = (typeof DENOMINATIONS)[number];

/**
 * The controlled shape. Keys are denomination paise (e.g. `50000` = ₹500);
 * values are non-negative integer piece counts.
 */
export type DenominationCounts = Record<Denomination, number>;

export const EMPTY_COUNTS: DenominationCounts = {
  50000: 0,
  20000: 0,
  10000: 0,
  5000: 0,
  2000: 0,
  1000: 0,
  500: 0,
  200: 0,
  100: 0,
};

/** Total counted in paise across all rows. Integer math only. */
export function totalPaise(counts: DenominationCounts): bigint {
  let sum = 0n;
  for (const denom of DENOMINATIONS) {
    sum += BigInt(denom) * BigInt(counts[denom] || 0);
  }
  return sum;
}

/** Total piece count (sum of `pieces` across rows). */
export function totalPieces(counts: DenominationCounts): number {
  let sum = 0;
  for (const denom of DENOMINATIONS) sum += counts[denom] || 0;
  return sum;
}

interface RowMeta {
  /** Display label, e.g. `₹500`. */
  chip: string;
  /** Long form, e.g. `Five hundred`. */
  long: string;
  /** `Banknote` or `Coin`. */
  kind: "Banknote" | "Coin";
  /** Tailwind classes for the chip pill (matches `.denom-chip` tones). */
  chipClass: string;
}

const ROW_META: Record<Denomination, RowMeta> = {
  50000: {
    chip: "₹500",
    long: "Five hundred",
    kind: "Banknote",
    chipClass: "bg-warn-50 text-warn border-warn-100",
  },
  20000: {
    chip: "₹200",
    long: "Two hundred",
    kind: "Banknote",
    chipClass: "bg-warn-50 text-warn border-warn-100",
  },
  10000: {
    chip: "₹100",
    long: "One hundred",
    kind: "Banknote",
    chipClass: "bg-sky-50 text-sky-700 border-sky-100",
  },
  5000: {
    chip: "₹50",
    long: "Fifty",
    kind: "Banknote",
    chipClass: "bg-bad-50 text-bad border-bad-100",
  },
  2000: {
    chip: "₹20",
    long: "Twenty",
    kind: "Banknote",
    chipClass: "bg-ok-50 text-ok border-ok-100",
  },
  1000: {
    chip: "₹10",
    long: "Ten",
    kind: "Banknote",
    chipClass: "bg-sky-50 text-sky-700 border-sky-100",
  },
  500: { chip: "₹5", long: "Five", kind: "Coin", chipClass: "bg-raised text-ink-2 border-line" },
  200: { chip: "₹2", long: "Two", kind: "Coin", chipClass: "bg-raised text-ink-2 border-line" },
  100: { chip: "₹1", long: "One", kind: "Coin", chipClass: "bg-raised text-ink-2 border-line" },
};

export interface DenominationInputProps {
  value: DenominationCounts;
  onChange: (next: DenominationCounts) => void;
}

export function DenominationInput({ value, onChange }: DenominationInputProps) {
  const grandPaise = totalPaise(value);
  const grandPieces = totalPieces(value);

  function setRow(denom: Denomination, pieces: number) {
    onChange({ ...value, [denom]: Math.max(0, Math.floor(pieces || 0)) });
  }

  return (
    <div className="border border-line rounded-md overflow-hidden bg-paper">
      <table className="w-full text-sm" aria-label="Denomination breakdown">
        <thead className="bg-raised border-b border-line">
          <tr className="text-xs uppercase tracking-wider text-muted">
            <th className="text-left px-3 py-2 font-semibold">Denom</th>
            <th className="text-right px-3 py-2 font-semibold">Pieces</th>
            <th className="text-right px-3 py-2 font-semibold">Subtotal</th>
          </tr>
        </thead>
        <tbody>
          {DENOMINATIONS.map((denom) => {
            const meta = ROW_META[denom];
            const pieces = value[denom] || 0;
            const subPaise = BigInt(denom) * BigInt(pieces);
            return (
              <tr key={denom} className="border-b border-line last:border-b-0">
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2.5">
                    <span
                      aria-hidden
                      className={cn(
                        "inline-grid place-items-center w-11 h-[26px] rounded-sm font-mono font-bold text-xs border",
                        meta.chipClass,
                      )}
                    >
                      {meta.chip}
                    </span>
                    <span>
                      <span className="text-sm font-semibold text-ink">{meta.long}</span>
                      <span className="block text-xs text-muted">{meta.kind}</span>
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    inputMode="numeric"
                    value={pieces === 0 ? "" : pieces.toString()}
                    onChange={(e) => setRow(denom, Number(e.target.value))}
                    aria-label={`Pieces of ${meta.chip}`}
                    className="w-24 ml-auto text-right num"
                  />
                </td>
                <td className="px-3 py-2 text-right num tabular-nums text-ink-2">
                  {(Number(subPaise) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="bg-raised border-t border-line">
          <tr className="text-sm font-semibold">
            <td className="px-3 py-2.5 text-left text-muted uppercase tracking-wider text-xs">
              Total
            </td>
            <td className="px-3 py-2.5 text-right num tabular-nums">{grandPieces} pcs</td>
            <td className="px-3 py-2.5 text-right num tabular-nums text-ink">
              {(Number(grandPaise) / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
